import { useEffect, useState, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import LifecycleBadge from '../components/common/LifecycleBadge';
import { adminDataQualityApi } from '../api/services';
import toast from 'react-hot-toast';
import { format } from 'date-fns';
import {
  AlertTriangle, MapPin, Sparkles, ShieldAlert, Loader2,
  ArrowUpRight, Package, Inbox, Truck, HelpCircle,
} from 'lucide-react';

const TILES = [
  { key: 'blocked_troublesome',  label: 'Blocked — troublesome',  icon: ShieldAlert,  color: 'red'     },
  { key: 'data_incomplete',      label: 'Data incomplete',        icon: HelpCircle,   color: 'amber'   },
  { key: 'reship_unlinked',      label: 'Reship — needs linking', icon: Package,      color: 'purple'  },
  { key: 'missing_zip',          label: 'Missing zip',            icon: MapPin,       color: 'orange'  },
  { key: 'special_char_address', label: 'Suspect address',        icon: AlertTriangle, color: 'orange'},
  { key: 'ops_pending',          label: 'With Ops',               icon: Inbox,        color: 'blue'    },
  { key: 'hold_active',          label: 'On hold',                icon: Sparkles,     color: 'yellow'  },
  { key: 'in_transit',           label: 'In transit',             icon: Truck,        color: 'sky'     },
];

const TILE_STYLES = {
  red:     'bg-red-50 border-red-200 text-red-900',
  amber:   'bg-amber-50 border-amber-200 text-amber-900',
  purple:  'bg-purple-50 border-purple-200 text-purple-900',
  orange:  'bg-orange-50 border-orange-200 text-orange-900',
  blue:    'bg-blue-50 border-blue-200 text-blue-900',
  yellow:  'bg-yellow-50 border-yellow-200 text-yellow-900',
  sky:     'bg-sky-50 border-sky-200 text-sky-900',
};

const TABS = [
  { key: 'flagged',          label: 'Flagged intake' },
  { key: 'blocked',          label: 'Blocked customers' },
  { key: 'virtual',          label: 'Virtual products' },
];

export default function DataQuality() {
  const [params] = useSearchParams();
  const initialTab = ['flagged', 'blocked', 'virtual'].includes(params.get('tab'))
    ? params.get('tab') : 'flagged';
  const [summary, setSummary] = useState({});
  const [summaryLoading, setSummaryLoading] = useState(true);
  const [tab, setTab] = useState(initialTab);

  const loadSummary = useCallback(() => {
    setSummaryLoading(true);
    adminDataQualityApi.summary()
      .then((res) => setSummary(res.data || {}))
      .catch((err) => toast.error(err.response?.data?.error || 'Failed to load summary'))
      .finally(() => setSummaryLoading(false));
  }, []);
  useEffect(() => { loadSummary(); }, [loadSummary]);

  return (
    <div>

      <div className="p-6">
        {/* Summary tiles */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
          {TILES.map((t) => {
            const Icon = t.icon;
            const n = summary[t.key];
            return (
              <div key={t.key} className={`rounded-xl border p-3 ${TILE_STYLES[t.color]}`}>
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-[11px] uppercase tracking-wide opacity-70">{t.label}</div>
                    <div className="text-2xl font-semibold mt-1">
                      {summaryLoading ? '…' : (n ?? 0)}
                    </div>
                  </div>
                  <Icon className="w-5 h-5 opacity-60" />
                </div>
              </div>
            );
          })}
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-4">
          {TABS.map((t) => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              className={`px-4 py-1.5 rounded-lg text-sm font-medium border ${
                tab === t.key
                  ? 'bg-blue-50 border-blue-300 text-blue-700'
                  : 'bg-white border-gray-200 text-gray-700 hover:bg-gray-50'
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {tab === 'flagged'  && <FlaggedIntakeTable onAction={loadSummary} />}
        {tab === 'blocked'  && <BlockedCustomersTable />}
        {tab === 'virtual'  && <VirtualProductsTable />}
      </div>
    </div>
  );
}

// --- Flagged intake (address flags) ----------------------------------------

function FlaggedIntakeTable({ onAction }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [flag, setFlag] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [raising, setRaising] = useState(false);

  async function load() {
    setLoading(true);
    try {
      const params = {};
      if (flag) params.flag = flag;
      const res = await adminDataQualityApi.flagged(params);
      setRows(res.data.rows || []);
      setSelected(new Set());
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to load flagged orders');
    } finally { setLoading(false); }
  }
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [flag]);

  // Allow raising any pre-dispatch flagged order to Ops — not only intake.
  // An in_review order with a bad address still benefits from Ops calling
  // the customer before it ships.
  const PRE_DISPATCH_STAGES = ['intake', 'in_review', 'reship_unlinked', 'data_incomplete'];
  const eligible = rows.filter(
    (r) => PRE_DISPATCH_STAGES.includes(r.lifecycle_stage) && (r.has_missing_zip || r.has_special_char_address)
  );

  function toggle(id) {
    const next = new Set(selected);
    next.has(id) ? next.delete(id) : next.add(id);
    setSelected(next);
  }
  function toggleAll() {
    if (selected.size === eligible.length) setSelected(new Set());
    else setSelected(new Set(eligible.map((r) => r.id)));
  }

  async function raiseToOps() {
    if (selected.size === 0) { toast.error('Pick at least one order'); return; }
    setRaising(true);
    try {
      const res = await adminDataQualityApi.raiseToOps({ orderIds: Array.from(selected) });
      const { raised, skipped } = res.data;
      toast.success(`Raised ${raised} to Ops${skipped.length ? ` (${skipped.length} skipped)` : ''}`);
      await load();
      onAction?.();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Bulk raise failed');
    } finally { setRaising(false); }
  }

  return (
    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <span className="text-xs text-gray-500">Filter:</span>
          {[
            { k: null,           l: 'All' },
            { k: 'missing_zip',  l: 'Missing zip' },
            { k: 'special_char', l: 'Suspect address' },
            { k: 'virtual',      l: 'Virtual product' },
          ].map((f) => (
            <button
              key={f.k || 'all'}
              onClick={() => setFlag(f.k)}
              className={`px-2.5 py-1 rounded-full text-xs font-medium ${
                flag === f.k ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              {f.l}
            </button>
          ))}
        </div>
        <button
          onClick={raiseToOps}
          disabled={raising || selected.size === 0}
          className="flex items-center gap-2 px-3 py-1.5 bg-orange-600 text-white rounded-lg text-sm font-medium hover:bg-orange-700 disabled:opacity-50"
        >
          {raising && <Loader2 className="w-4 h-4 animate-spin" />}
          <ArrowUpRight className="w-4 h-4" />
          Raise {selected.size} to Ops
        </button>
      </div>

      {loading ? (
        <div className="p-12 text-center text-gray-500">
          <Loader2 className="w-5 h-5 animate-spin inline mr-2" /> Loading…
        </div>
      ) : rows.length === 0 ? (
        <div className="p-12 text-center text-gray-500 text-sm">No flagged orders.</div>
      ) : (
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-xs uppercase text-gray-500">
            <tr>
              <th className="px-3 py-2 w-8">
                <input type="checkbox" checked={selected.size === eligible.length && eligible.length > 0}
                       onChange={toggleAll} />
              </th>
              <th className="text-left px-3 py-2">Order</th>
              <th className="text-left px-3 py-2">Customer</th>
              <th className="text-left px-3 py-2">Address</th>
              <th className="text-left px-3 py-2">Flags</th>
              <th className="text-left px-3 py-2">Stage</th>
              <th className="text-left px-3 py-2">Created</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {rows.map((r) => {
              const canSelect = PRE_DISPATCH_STAGES.includes(r.lifecycle_stage) && (r.has_missing_zip || r.has_special_char_address);
              return (
                <tr key={r.id} className="hover:bg-gray-50">
                  <td className="px-3 py-2">
                    {canSelect && (
                      <input type="checkbox" checked={selected.has(r.id)} onChange={() => toggle(r.id)} />
                    )}
                  </td>
                  <td className="px-3 py-2 font-mono text-xs">
                    <Link to={`/orders/${r.id}`} className="text-blue-600 hover:underline">
                      {r.order_id_external}
                    </Link>
                  </td>
                  <td className="px-3 py-2">{r.customer_name}</td>
                  <td className="px-3 py-2 text-xs text-gray-600">
                    {r.shipping_address1}
                    {r.shipping_address2 && `, ${r.shipping_address2}`}
                    <div className="text-gray-500">
                      {r.shipping_city}, {r.shipping_zone} {r.shipping_postcode} {r.shipping_country}
                    </div>
                  </td>
                  <td className="px-3 py-2">
                    <div className="flex flex-wrap gap-1">
                      {r.has_missing_zip && (
                        <span className="text-[10px] bg-orange-100 text-orange-800 px-1.5 py-0.5 rounded">no zip</span>
                      )}
                      {r.has_special_char_address && (
                        <span className="text-[10px] bg-orange-100 text-orange-800 px-1.5 py-0.5 rounded">special char</span>
                      )}
                      {r.has_virtual_product && (
                        <span className="text-[10px] bg-amber-100 text-amber-800 px-1.5 py-0.5 rounded">virtual</span>
                      )}
                    </div>
                  </td>
                  <td className="px-3 py-2"><LifecycleBadge stage={r.lifecycle_stage} ownerTeam={r.current_owner_team} /></td>
                  <td className="px-3 py-2 text-xs text-gray-500">
                    {r.created_at && format(new Date(r.created_at), 'dd MMM HH:mm')}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
}

// --- Blocked troublesome customers ------------------------------------------

function BlockedCustomersTable() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    adminDataQualityApi.blockedCustomers()
      .then((res) => setRows(res.data.rows || []))
      .catch((err) => toast.error(err.response?.data?.error || 'Failed to load'))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return (
    <div className="bg-white rounded-xl border border-gray-200 p-12 text-center text-gray-500">
      <Loader2 className="w-5 h-5 animate-spin inline mr-2" /> Loading…
    </div>
  );

  return (
    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
      {rows.length === 0 ? (
        <div className="p-12 text-center text-gray-500 text-sm">No blocked orders.</div>
      ) : (
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-xs uppercase text-gray-500">
            <tr>
              <th className="text-left px-3 py-2">Order</th>
              <th className="text-left px-3 py-2">Customer</th>
              <th className="text-left px-3 py-2">Past unpaid</th>
              <th className="text-left px-3 py-2">First flagged</th>
              <th className="text-left px-3 py-2">Waiting since</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {rows.map((r) => (
              <tr key={r.id} className="hover:bg-gray-50">
                <td className="px-3 py-2 font-mono text-xs">
                  <Link to={`/orders/${r.id}`} className="text-blue-600 hover:underline">
                    {r.order_id_external}
                  </Link>
                </td>
                <td className="px-3 py-2">{r.customer_name}</td>
                <td className="px-3 py-2">
                  <span className="text-xs bg-red-100 text-red-800 px-2 py-0.5 rounded">
                    {r.troublesome_count}
                  </span>
                </td>
                <td className="px-3 py-2 text-xs text-gray-600">
                  {r.troublesome_first_flagged_at
                    ? format(new Date(r.troublesome_first_flagged_at), 'dd MMM yyyy')
                    : '—'}
                </td>
                <td className="px-3 py-2 text-xs text-gray-500">
                  {r.current_owner_since && format(new Date(r.current_owner_since), 'dd MMM HH:mm')}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

// --- Virtual product triage ------------------------------------------------

function VirtualProductsTable() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [subQueue, setSubQueue] = useState(null);

  async function load() {
    setLoading(true);
    try {
      const params = {};
      if (subQueue) params.sub_queue = subQueue;
      const res = await adminDataQualityApi.virtualProducts(params);
      setRows(res.data.rows || []);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to load');
    } finally { setLoading(false); }
  }
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [subQueue]);

  return (
    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
      <div className="flex items-center gap-2 px-4 py-3 border-b border-gray-200">
        <span className="text-xs text-gray-500">Sub-queue:</span>
        {[
          { k: null,    l: 'All' },
          { k: 'email', l: 'Email' },
          { k: 'cc',    l: 'CC' },
        ].map((s) => (
          <button key={s.k || 'all'} onClick={() => setSubQueue(s.k)}
                  className={`px-2.5 py-1 rounded-full text-xs font-medium ${
                    subQueue === s.k ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}>
            {s.l}
          </button>
        ))}
      </div>
      {loading ? (
        <div className="p-12 text-center text-gray-500">
          <Loader2 className="w-5 h-5 animate-spin inline mr-2" /> Loading…
        </div>
      ) : rows.length === 0 ? (
        <div className="p-12 text-center text-gray-500 text-sm">No virtual-product orders pending triage.</div>
      ) : (
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-xs uppercase text-gray-500">
            <tr>
              <th className="text-left px-3 py-2">Order</th>
              <th className="text-left px-3 py-2">Customer</th>
              <th className="text-left px-3 py-2">Email</th>
              <th className="text-left px-3 py-2">Virtual items</th>
              <th className="text-left px-3 py-2">Sub-queue</th>
              <th className="text-left px-3 py-2">Waiting</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {rows.map((r) => (
              <tr key={r.id} className="hover:bg-gray-50">
                <td className="px-3 py-2 font-mono text-xs">
                  <Link to={`/orders/${r.id}`} className="text-blue-600 hover:underline">
                    {r.order_id_external}
                  </Link>
                </td>
                <td className="px-3 py-2">{r.customer_name}</td>
                <td className="px-3 py-2 text-xs text-gray-600">{r.customer_email || '—'}</td>
                <td className="px-3 py-2">
                  <span className="text-xs bg-amber-100 text-amber-800 px-2 py-0.5 rounded">
                    {r.virtual_item_count}
                  </span>
                </td>
                <td className="px-3 py-2 text-xs capitalize">{r.ops_sub_queue || '—'}</td>
                <td className="px-3 py-2 text-xs text-gray-500">
                  {r.current_owner_since && format(new Date(r.current_owner_since), 'dd MMM HH:mm')}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
